import React, { FormEvent, useEffect, useState } from 'react'

import useSaveCustomNotification from '../../../hooks/custom_notifications/useSaveCustomNotification';
import useDeleteCustomNotification from '../../../hooks/custom_notifications/useDeleteCustomNotification';

import { ICustomNotification, ISaveCustomNotification, TCustomNotificationStatus, TLogicalOperator } from '../../../types/custom_notifications';
import useGetActions from '../../../hooks/action/useGetActions';

interface ICustomNotificationProps {
    customNotification: ICustomNotification;
    currOption: number[];
    setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
    isDelete: boolean;
    setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
    setIsOpenTestNotificationModal: React.Dispatch<React.SetStateAction<boolean>>;
    setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
    disableDelete: boolean;
    setIsResetNew: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteModalMessage: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
}

const CustomNotification = ({ customNotification, setIsOpenTestNotificationModal, currOption, setCurrOption, isDelete, setIsDelete, setIsOpenDeleteModal, disableDelete, setIsResetNew, setDeleteModalMessage }: ICustomNotificationProps) => {

    const [title, setTitle] = useState<string>(customNotification.notification.id ? `[${customNotification.method}] ${customNotification.notification.title}` : 'New Custom Notification');
    const [currentCustomNotification, setCurrentCustomNotification] = useState<ICustomNotification>(customNotification);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [apiError, setApiError] = useState<string | null>(null); //eslint-disable-line

    const [isNotificationSent, setIsNotificationSent] = useState<boolean>(false);

    const { data: actions, isLoading: isActionLoading } = useGetActions();

    const { mutate: saveCustomNotification } = useSaveCustomNotification();
    const { mutate: deleteCustomNotification } = useDeleteCustomNotification();

    useEffect(() => {
        if (customNotification && customNotification?.notification?.user_notifications?.length) {
            setIsNotificationSent(true);
        } else {
            setIsNotificationSent(false);
        }
        setCurrentCustomNotification(customNotification);
        setTitle(customNotification.notification.id ? `[${customNotification.method}] ${customNotification.notification.title}` : 'New Custom Notification');
    }, [customNotification]);


    useEffect(() => {
        if (isDelete) {
            setCurrOption([0]);
            setIsResetNew(true);
            if (!customNotification.id) return;
            deleteCustomNotification({
                id: customNotification.id,
                setIsProcessing,
                setApiError

            });
            setIsDelete(false);
        }
    }, [isDelete]); //eslint-disable-line


    if (!currentCustomNotification) {
        return <p>Loading...</p>
    }

    const handleSave = (e: FormEvent) => {
        e.preventDefault();

        const payload: ISaveCustomNotification = {
            id: currentCustomNotification.id ?? null,
            status: currentCustomNotification.status,
            method: currentCustomNotification.method,
            notification_id: currentCustomNotification.notification.id ?? null,
            title: currentCustomNotification.notification.title,
            description: currentCustomNotification.notification.description,
            action_id: currentCustomNotification.notification.action_id ?? null
        }

        saveCustomNotification({
            data: payload,
            setIsProcessing,
            setApiError
        });

        setTitle(`[${currentCustomNotification.method}] Custom Notification` || 'New Custom Notification');
    }

    const handleDelete = () => {
        if (!customNotification.id) {
            setIsResetNew(true);
            setCurrOption([0]);
        } else {
            setIsOpenDeleteModal(true);
            setDeleteModalMessage({ title: 'Delete Custom Notification', subtitle: 'This will delete all the associated components, functions and parameters. Are you sure you want to delete this custom notification?' });
        }
    }

    const handleSend = () => {
        setIsOpenTestNotificationModal(true);
    }

    return (
        <>
            <div className='imm_admin__content'>
                <div className='content_head'>
                    <h3>{title}</h3>
                </div>

                <form onSubmit={handleSave} className='content_form'>
                    <div className='field'>
                        <label htmlFor='title'>Title <span className='field__required'>*</span></label>
                        <input id='title' name='title' placeholder='Enter Title' value={currentCustomNotification.notification.title || ''} onChange={(e) => setCurrentCustomNotification({ ...currentCustomNotification, notification: { ...currentCustomNotification.notification, title: e.target.value } })} />
                    </div>
                    <div className='field'>
                        <label htmlFor='description'>Description <span className='field__required'>*</span></label>
                        <textarea id='description' name='description' placeholder='Enter Description' value={currentCustomNotification.notification.description || ""} onChange={(e) => setCurrentCustomNotification({ ...currentCustomNotification, notification: { ...currentCustomNotification.notification, description: e.target.value } })}></textarea>
                    </div>
                    {isActionLoading ?
                        <div>Loading actions...</div> : actions && actions.length > 0 &&
                        <div className='field'>
                            <label htmlFor='action'>Action <span className='field__required'>*</span></label>
                            <select id='action' name='action' value={currentCustomNotification.notification.action_id || ''} onChange={(e) => setCurrentCustomNotification({ ...currentCustomNotification, notification: { ...currentCustomNotification.notification, action_id: parseInt(e.target.value) } })}>
                                <option value=''>Select action</option>
                                {actions.map((action, index) => (
                                    <option key={index} value={action.id}>{action.name}</option>
                                ))}
                            </select>
                        </div>
                    }
                    <div className='field'>
                        <label htmlFor='method'>Method <span className='field__required'>*</span></label>
                        <select id='method' name='method' value={currentCustomNotification.method} onChange={(e) => setCurrentCustomNotification({ ...currentCustomNotification, method: e.target.value as TLogicalOperator })}>
                            <option value={'and'}>and</option>
                            <option value={'or'}>or</option>
                        </select>
                    </div>
                    <div className='field'>
                        <label htmlFor='status'>Status <span className='field__required'>*</span></label>
                        <select id='status' name='status' value={currentCustomNotification.status} onChange={(e) => setCurrentCustomNotification({ ...currentCustomNotification, status: e.target.value as TCustomNotificationStatus })}>
                            <option value={'active'}>Active</option>
                            <option value={'inactive'}>Inactive</option>
                        </select>
                    </div>

                    <div className='actions'>
                        <button disabled={isNotificationSent || disableDelete || isProcessing} className='delete_btn' type="button" onClick={handleDelete}>{!customNotification.id ? 'Cancel' : 'Delete'}</button>
                        <button className='save_btn' type='submit'>{isProcessing ? <i className='imm_admin__process_loader' /> : !customNotification.id ? 'Create' : 'Save'}</button>
                        <button className='test_btn color_success' type='button' disabled={customNotification.status === 'inactive' || !currentCustomNotification.id} onClick={handleSend}>Send</button>
                    </div>
                </form>
            </div>
        </>
    );
}
export default CustomNotification;